// /reports/kitchen-performance — Kitchen Performance (PAGES.md §3.53) — RESTAURANT

import { useState, useMemo } from 'react';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { Badge, HeatmapChart } from '@bbpos/ui';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportKpiRow,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  useReportQuery,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
  KpiIcons,
} from '@/features/reports';
import type { KitchenStationRow } from '@/features/reports';

type Row = KitchenStationRow & { id: string };

const columns: ColumnDef<Row>[] = [
  { key: 'station', header: 'Station', render: (_v, row) => <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.station}</span> },
  { key: 'avg_prep_time_sec', header: 'Avg Prep Time', numeric: true, render: (_v, row) => <span className="tabular-nums">{Math.round(row.avg_prep_time_sec / 60)} min</span> },
  { key: 'items_count', header: 'Items', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.items_count.toLocaleString('en-IN')}</span> },
  { key: 'delayed_count', header: 'Delayed', numeric: true, render: (_v, row) => row.delayed_count > 0 ? <Badge variant="danger" size="sm">{row.delayed_count}</Badge> : <Badge variant="success" size="sm">0</Badge> },
];

export function KitchenPerformanceReportPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const filter = useReportFilter(timeRange, locationId);

  const { data, isLoading, error } = useReportQuery({
    key: 'kitchen-performance',
    filter,
    queryFn: (f) => reportsApi.getKitchenPerformance(f),
  });

  const rows: Row[] = useMemo(
    () => (data?.stations ?? []).map((s) => ({ ...s, id: s.station })),
    [data],
  );

  const heatmapCells = useMemo(
    () =>
      rows.map((s) => ({
        row: s.station,
        col: 'Prep Time (min)',
        value: Math.round(s.avg_prep_time_sec / 60),
      })),
    [rows],
  );

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      downloadCsv(
        `kitchen-performance-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Station', get: (r: Row) => r.station },
          { header: 'Avg Prep (sec)', get: (r: Row) => r.avg_prep_time_sec },
          { header: 'Items', get: (r: Row) => r.items_count },
          { header: 'Delayed', get: (r: Row) => r.delayed_count },
        ],
        rows,
      );
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(fmt, 'Orio360 Kitchen Performance', `Avg prep: ${Math.round((data?.avg_prep_time_sec ?? 0) / 60)} min • ${(data?.delayed_pct ?? 0).toFixed(1)}% delayed`);
    }
  };

  return (
    <ReportPageShell
      title="Kitchen Performance"
      subtitle="Prep time by station, items per hour and delay tracking."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'Kitchen Performance' }]}
      accent="violet-pink"
      headerActions={<ReportExportMenu onExport={handleExport} />}
      filterBar={<ReportFilterBar timeRange={timeRange} onTimeRangeChange={setTimeRange} locationId={locationId} onLocationChange={setLocationId} />}
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={4} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          <ReportKpiRow
            tiles={[
              { id: 'prep', title: 'Avg Prep Time', value: `${Math.round((data?.avg_prep_time_sec ?? 0) / 60)} min`, tone: 'violet', icon: KpiIcons.clock },
              { id: 'iph', title: 'Items / Hour', value: (data?.items_per_hour ?? 0).toFixed(0), tone: 'pink', icon: KpiIcons.fire },
              { id: 'delayed', title: 'Delayed %', value: `${(data?.delayed_pct ?? 0).toFixed(1)}%`, tone: (data?.delayed_pct ?? 0) > 10 ? 'danger' : 'success', icon: KpiIcons.percent },
              { id: 'stations', title: 'Stations', value: String(rows.length), tone: 'indigo', icon: KpiIcons.tag },
            ]}
          />
          {heatmapCells.length > 0 ? (
            <div className="rounded-2xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md p-5 mb-6 animate-slide-up">
              <HeatmapChart data={heatmapCells} title="Prep Time by Station (minutes)" rowLabel="Station" colLabel="Metric" />
            </div>
          ) : null}
          <ReportTable title="Per-Station Metrics" columns={columns} data={rows} emptyTitle="No kitchen data for selected period" totalCount={rows.length} />
        </>
      )}
    </ReportPageShell>
  );
}
